<script setup>
/**
 * The approved wording one WhatsApp number may send, and the blanks each one expects.
 *
 * WHY IT IS ON THE SETUP PAGE AT ALL: an owner's own system asks for a message BY NAME,
 * and the name is Meta's (`eid_offer_v2_final`), not hers. Without this list the person
 * wiring the integration has to be told the name out of band, and a name typed from
 * memory fails at send time with a refusal about a template that "does not exist" — after
 * the call, in production, on a real customer.
 *
 * TEMPLATES ARE APPROVED PER SENDER, so this is scoped to one number and the number is
 * chosen here. A list shown under the wrong number is not a smaller answer, it is a wrong
 * one — which is why the BFF echoes the `inboxId` it answered for and the page routes the
 * response back to the sender that asked.
 *
 * THE REFRESH TELLS THE TRUTH ABOUT ITSELF. `POST /templates/sync` asks the platform to
 * go and ask WhatsApp; upstream that is background work and it can be refused outright
 * (`InboxPolicy#sync_templates?`). So the answer's own `note` is rendered verbatim rather
 * than the screen claiming the list below it is now current — and the list is deliberately
 * NOT re-read on the way back, because a read taken a millisecond after the request would
 * return exactly what is already on screen and would look like the refresh did nothing.
 *
 * THE BODY IS SHOWN, NOT JUST THE NAME. Same finding as the broadcast composer's
 * `TemplateChoice`: the name tells the owner nothing about what her customer would read.
 * It is other people's text, often Arabic, so it gets `dir="auto"`, `pre-wrap`, a height
 * cap and its own scroll container — README §2, where an unbounded block stretched a page
 * to 2.6x the viewport.
 */
import Button from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';

defineProps({
  /** `[{ value, label }]` — the workspace's sending numbers, resolved by the page. */
  senderOptions: { type: Array, default: () => [] },
  /** The chosen inbox id, as a select value (a string). */
  modelValue: { type: String, default: '' },
  templates: { type: Array, default: () => [] },
  isFetching: { type: Boolean, default: false },
  /** The templates read failed. Its own state: "we could not look" is not "there are none". */
  hasError: { type: Boolean, default: false },
  /**
   * F3.3: the workspace is not set up for approved messages yet (409 `broadcast_not_provisioned`).
   * A state, not a failure, so it outranks `hasError` and offers nothing to retry.
   */
  notSetUp: { type: Boolean, default: false },
  isSyncing: { type: Boolean, default: false },
  /** The server's own sentence about what the refresh actually did. */
  syncNote: { type: String, default: '' },
  syncError: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'refresh']);

const { t } = useWasloI18n();

/** P-4.3: one line naming the buttons, marking the ones a developer must send a value for. */
const buttonsLine = template =>
  (template.buttons ?? [])
    .map(button =>
      button.needsValue
        ? t('WASLO.OUTBOUND.SETUP.TEMPLATES.BUTTON_NEEDS_VALUE', {
            text: button.text,
          })
        : button.text
    )
    .join(t('WASLO.OUTBOUND.SETUP.TEMPLATES.LIST_JOIN'));
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
    data-waslo-outbound-templates
  >
    <!-- README §3: the select gets the flexible half of the row, the button the fixed. -->
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
      <WasloSelect
        class="flex-1 min-w-0"
        :model-value="modelValue"
        :label="t('WASLO.OUTBOUND.SETUP.TEMPLATES.SENDER_LABEL')"
        :options="senderOptions"
        :placeholder="t('WASLO.OUTBOUND.COMMON.SENDER_ANY')"
        data-waslo-outbound-templates-sender
        @update:model-value="value => emit('update:modelValue', value)"
      />
      <Button
        class="shrink-0"
        :label="
          isSyncing
            ? t('WASLO.OUTBOUND.SETUP.TEMPLATES.REFRESHING')
            : t('WASLO.OUTBOUND.SETUP.TEMPLATES.REFRESH')
        "
        icon="i-lucide-refresh-cw"
        variant="faded"
        color="slate"
        size="sm"
        :is-loading="isSyncing"
        :disabled="isSyncing || modelValue === ''"
        data-waslo-outbound-templates-refresh
        @click="emit('refresh')"
      />
    </div>

    <Banner v-if="syncNote" color="blue" data-waslo-outbound-templates-note>
      {{ syncNote }}
    </Banner>
    <Banner
      v-if="syncError"
      color="ruby"
      data-waslo-outbound-templates-sync-error
    >
      {{ syncError }}
    </Banner>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-8 text-n-slate-11"
      data-waslo-outbound-templates-loading
    >
      <Spinner />
    </div>

    <p
      v-else-if="notSetUp"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      data-waslo-outbound-templates-not-set-up
    >
      {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.NOT_SET_UP') }}
    </p>

    <p
      v-else-if="hasError"
      class="mb-0 text-sm leading-6 text-n-ruby-9"
      data-waslo-outbound-templates-error
    >
      {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.UNAVAILABLE') }}
    </p>

    <p
      v-else-if="templates.length === 0"
      class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
      data-waslo-outbound-templates-empty
    >
      {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.EMPTY') }}
    </p>

    <template v-else>
      <ul class="flex flex-col gap-3 mb-0 list-none">
        <li
          v-for="template in templates"
          :key="`${template.name}:${template.language}`"
          class="flex flex-col gap-2 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
          :data-waslo-outbound-template="template.name"
        >
          <div class="flex flex-col min-w-0 gap-1">
            <span class="text-xs text-n-slate-10">
              {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.NAME') }}
            </span>
            <!--
              The exact stored spelling of both the name and the language: Chatwoot matches
              the language exactly, so an owner who copies a lowercased "ar" out of this
              page builds a call that dies at send.
            -->
            <span
              class="min-w-0 font-mono text-sm break-all text-n-slate-12"
              dir="ltr"
              data-waslo-outbound-template-name
            >
              {{ template.name }}
            </span>
          </div>

          <p
            class="mb-0 overflow-y-auto text-sm leading-6 break-words whitespace-pre-wrap max-h-40 text-n-slate-11"
            dir="auto"
            data-waslo-outbound-template-body
          >
            {{ template.body }}
          </p>

          <div
            class="flex flex-wrap items-center min-w-0 gap-x-3 gap-y-1 text-xs text-n-slate-10"
          >
            <span>
              {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.LANGUAGE') }}
              <bdi>{{ template.language }}</bdi>
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.CATEGORY') }}
              <bdi>{{ template.category }}</bdi>
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.STATUS') }}
              <bdi>{{ template.status }}</bdi>
            </span>
            <span aria-hidden="true">·</span>
            <span data-waslo-outbound-template-blanks>
              {{
                template.variables.length === 0
                  ? t('WASLO.OUTBOUND.SETUP.TEMPLATES.NO_BLANKS')
                  : t('WASLO.OUTBOUND.SETUP.TEMPLATES.BLANKS', {
                      count: template.variables.length,
                    })
              }}
            </span>
          </div>
          <p
            v-if="(template.buttons ?? []).length"
            class="mb-0 text-xs text-n-slate-10"
            dir="auto"
            data-waslo-outbound-template-buttons
          >
            {{
              t('WASLO.OUTBOUND.SETUP.TEMPLATES.BUTTONS', {
                list: buttonsLine(template),
              })
            }}
          </p>
        </li>
      </ul>

      <p class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10">
        {{ t('WASLO.OUTBOUND.SETUP.TEMPLATES.LANGUAGE_HINT') }}
      </p>
    </template>
  </div>
</template>
